/**
 * Warnton für kritische Benachrichtigungen (zwei kurze Beeps über die Web Audio API).
 *
 * Die Auslösung kommt aus dem Firestore-Notifications-Listener (serverseitige
 * Events mit data.priority === 'critical'), nicht aus einem eigenen Polling.
 *
 * Browser sperren den AudioContext, bis im jeweiligen Fenster eine Nutzergeste
 * stattgefunden hat. Früher wurde ein blockierter Ton auf einen dafür frisch
 * registrierten click/keydown-Listener geparkt und später nachgeholt. Das hatte
 * zwei Fehler: Ein Fenster, in dem nie geklickt wird — etwa die LiveMap auf dem
 * zweiten Monitor — sammelte beliebig viele solcher Listener an, und beim ersten
 * Klick feuerten sie alle zugleich. Ergebnis war eine Ton-Salve für längst
 * vergangene Ereignisse.
 *
 * Jetzt gilt: Es wird nichts nachgeholt. Ist der Context gesperrt, entfällt der
 * Ton — das Ereignis bleibt visuell sichtbar, was der ehrlichere Zustand ist.
 * Die Entsperrung läuft einmalig über den tabCoordinator, der dafür genau ein
 * Listener-Paar für die gesamte Sitzung hält.
 *
 * Konzept: docs/KONZEPT_MEHRFACHE_PORTAL_INSTANZEN.md
 */
import tabCoordinator from './tabCoordinator';

class AlertSoundService {
  constructor() {
    this.audioContext = null;
    // Erst bei der ersten Nutzergeste: Ein AudioContext, der beim Modul-Import
    // erzeugt würde, bliebe ohnehin suspendiert.
    tabCoordinator.onAudioUnlocked(() => this.resumeContext());
  }

  ensureContext() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!this.audioContext) {
      this.audioContext = new AudioContextClass();
    }
    return this.audioContext;
  }

  resumeContext() {
    try {
      const context = this.ensureContext();
      if (context && context.state === 'suspended') {
        context.resume().catch(() => {
          // Der nächste Ton versucht es erneut.
        });
      }
    } catch (error) {
      console.warn('AudioContext konnte nicht aktiviert werden:', error);
    }
  }

  playAlertSound() {
    try {
      const context = this.ensureContext();
      if (!context) return;

      if (context.state !== 'running') {
        // Bewusst kein Nachholen — siehe Dateikommentar.
        this.resumeContext();
        return;
      }

      this.playBeepSound();
    } catch (error) {
      console.error('Fehler beim Abspielen des Warntons:', error);
    }
  }

  playBeepSound() {
    try {
      this.playBeep(800);
      setTimeout(() => {
        if (this.audioContext && this.audioContext.state === 'running') {
          this.playBeep(600);
        }
      }, 200);
    } catch (error) {
      console.error('Fehler beim Beep-Sound:', error);
    }
  }

  playBeep(frequency) {
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.5);
  }
}

const alertSoundService = new AlertSoundService();

export default alertSoundService;
